import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Text,
  HStack,
  TableContainer,
  useColorModeValue,
} from '@chakra-ui/react';
import { Rock, User, Milestone, ActionItem, Issue } from '../../types';
import { calculateMilestoneProgress, formatCollaboratorTypes } from '../rocks.util';

interface RockWithDetails extends Rock {
  owner?: User;
  collaboratorUsers?: User[];
  milestones?: Milestone[];
  actionItems?: ActionItem[];
}

type CollaboratorWithTypes = {
  name: string;
  types: ('R' | 'T')[];
};

interface RocksTableViewProps {
  rocks: RockWithDetails[];
  users: User[];
  issues: Issue[];
  onRockClick: (rockId: string) => void;
}

/**
 * Get collaborators with their types (R = Rock collaborator, T = Task assignee)
 */
function getCollaboratorsWithTypes(
  rock: RockWithDetails,
  users: User[]
): CollaboratorWithTypes[] {
  const collaboratorMap: Record<string, CollaboratorWithTypes> = {};

  // Add rock collaborators (type R)
  if (rock.collaboratorUsers && rock.collaboratorUsers.length > 0) {
    rock.collaboratorUsers.forEach((user) => {
      if (user && user.name) {
        collaboratorMap[user.id] = { name: user.name, types: ['R'] };
      }
    });
  }

  // Add action item assignees (type T)
  if (rock.actionItems && rock.actionItems.length > 0) {
    const incompleteItems = rock.actionItems.filter((item) => !item.complete);

    incompleteItems.forEach((item) => {
      if (item.assigneeId) {
        const assignee = users.find((u) => u.id === item.assigneeId);
        if (assignee) {
          if (collaboratorMap[assignee.id]) {
            // User is already a collaborator, add T type
            if (!collaboratorMap[assignee.id].types.includes('T')) {
              collaboratorMap[assignee.id].types.push('T');
            }
          } else {
            // User is only a task assignee
            collaboratorMap[assignee.id] = { name: assignee.name, types: ['T'] };
          }
        }
      }
    });
  }

  return Object.values(collaboratorMap);
}

/**
 * Get issues for a specific rock
 */
function getRockIssues(rockId: string, issues: Issue[]): Issue[] {
  return issues.filter((issue) => issue.relatedRockId === rockId && issue.status !== 'solved');
}

/**
 * Rocks Table View Component
 * Displays rocks in a detailed table format
 * Migrated from Tailwind to Chakra UI
 */
export function RocksTableView({ rocks, users, issues, onRockClick }: RocksTableViewProps) {
  const emptyBg = useColorModeValue('white', 'gray.700');
  const emptyBorder = useColorModeValue('gray.200', 'gray.600');
  const emptyText = useColorModeValue('gray.500', 'gray.400');
  const emptyTextLight = useColorModeValue('gray.400', 'gray.500');
  const tableBg = useColorModeValue('white', 'gray.700');
  const tableBorder = useColorModeValue('gray.200', 'gray.600');
  const theadBg = useColorModeValue('gray.50', 'gray.800');
  const thColor = useColorModeValue('gray.500', 'gray.400');
  const rowHover = useColorModeValue('gray.50', 'gray.600');
  const rowBg = useColorModeValue('white', 'gray.700');
  const rowBgIssues = useColorModeValue('red.50', 'red.900');
  const textColor = useColorModeValue('gray.900', 'white');
  const textColorLight = useColorModeValue('gray.400', 'gray.500');

  if (rocks.length === 0) {
    return (
      <Box textAlign="center" py={12} bg={emptyBg} borderRadius="lg" border="1px" borderColor={emptyBorder}>
        <Text color={emptyText} fontSize="lg" mb={2}>
          No rocks found
        </Text>
        <Text color={emptyTextLight} fontSize="sm" mt={1}>
          Go to All Rocks to create your first 90-day goal
        </Text>
      </Box>
    );
  }

  return (
    <Box bg={tableBg} rounded="lg" border="1px" borderColor={tableBorder} overflow="hidden">
      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead bg={theadBg}>
            <Tr>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color={thColor} textTransform="uppercase" letterSpacing="wider">
                Owner
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color={thColor} textTransform="uppercase" letterSpacing="wider">
                Rock
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color={thColor} textTransform="uppercase" letterSpacing="wider">
                Status
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color={thColor} textTransform="uppercase" letterSpacing="wider">
                Progress
              </Th>
              <Th px={6} py={3} textAlign="left" fontSize="xs" fontWeight="medium" color={thColor} textTransform="uppercase" letterSpacing="wider">
                Issues
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rocks.map((rock) => {
              const rockIssues = getRockIssues(rock.id, issues);
              const collaborators = getCollaboratorsWithTypes(rock, users);
              const milestoneProgress = calculateMilestoneProgress(rock.milestones || []);

              return (
                <Tr
                  key={rock.id}
                  id={`rock-${rock.id}`}
                  _hover={{ bg: rowHover }}
                  borderLeft={rockIssues.length > 0 ? '4px solid' : 'none'}
                  borderLeftColor={rockIssues.length > 0 ? 'red.500' : 'transparent'}
                  bg={rockIssues.length > 0 ? rowBgIssues : rowBg}
                >
                  {/* Owner and Collaborators */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme="blue" px={2} py={1} fontSize="xs" fontWeight="medium" borderRadius="full">
                        {rock.owner?.name || 'Unknown'}
                      </Badge>
                      {collaborators.length > 0 &&
                        collaborators.map((collab) => (
                          <Badge
                            key={collab.name}
                            colorScheme="green"
                            px={2}
                            py={1}
                            fontSize="xs"
                            fontWeight="medium"
                            borderRadius="full"
                          >
                            {collab.name}
                            <Text as="sup" fontSize="xs" ml={0.5}>
                              {formatCollaboratorTypes(collab.types)}
                            </Text>
                          </Badge>
                        ))}
                    </HStack>
                  </Td>

                  {/* Rock Name */}
                  <Td px={6} py={4}>
                    <Text fontSize="sm" color={textColor}>
                      {rock.rock}
                    </Text>
                  </Td>

                  {/* Status */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    {rock.status === 'on-track' && (
                      <Badge
                        colorScheme="green"
                        px={2.5}
                        py={0.5}
                        fontSize="xs"
                        fontWeight="medium"
                        borderRadius="full"
                        display="inline-flex"
                        alignItems="center"
                      >
                        On Track
                      </Badge>
                    )}
                    {rock.status === 'off-track' && (
                      <Badge
                        colorScheme="red"
                        px={2.5}
                        py={0.5}
                        fontSize="xs"
                        fontWeight="medium"
                        borderRadius="full"
                        display="inline-flex"
                        alignItems="center"
                      >
                        Off Track
                      </Badge>
                    )}
                  </Td>

                  {/* Progress */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      {milestoneProgress}%
                    </Text>
                  </Td>

                  {/* Issues */}
                  <Td px={6} py={4} whiteSpace="nowrap">
                    {rockIssues.length > 0 ? (
                      <Button
                        size="sm"
                        variant="link"
                        colorScheme="red"
                        fontWeight="medium"
                        onClick={() => onRockClick(rock.id)}
                        cursor="pointer"
                      >
                        ⚠️ {rockIssues.length}
                      </Button>
                    ) : (
                      <Text color={textColorLight}>-</Text>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

